/**
 * Analysis Slice — Analysis retry, toggle, and error clearing
 *
 * Handles: retryAnalysis, toggleAnalysisExpanded, clearError
 */

import { AccessibilityInfo } from "react-native";
import type { StateCreator } from "zustand";
import { api } from "../../services/api";
import { HTTP_AI_TIMEOUT_MS } from "../../config/timeouts";
import { resultToErrorInfo } from "../../utils/conversationErrors";
import type { ConversationState } from "../conversationTypes";
import type { ConversationAnalysis } from "../../types/conversation";
import { log } from "../conversationHelpers";

export interface AnalysisSlice {
  isAnalyzing: boolean;
  error: string | null;
  errorInfo: ConversationState['errorInfo'];

  retryAnalysis: (messageId: string) => Promise<void>;
  toggleAnalysisExpanded: (messageId: string) => void;
  clearError: () => void;
}

export const createAnalysisSlice: StateCreator<
  ConversationState,
  [],
  [],
  AnalysisSlice
> = (set, get) => ({
  isAnalyzing: false,
  error: null,
  errorInfo: null,

  /**
   * Retry analysis only for a user message.
   * Used when the AI response succeeded but analysis failed (analysisError set).
   * Calls the session-aware analyze endpoint which saves results server-side.
   */
  retryAnalysis: async (messageId: string) => {
    const state = get();
    const message = state.messages.find((m) => m.id === messageId);
    const sessionId = state.currentSessionId;

    if (!message || !message.analysisError) {
      log.warn("Cannot retry analysis - no analysisError on message", {
        messageId,
        hasAnalysisError: !!message?.analysisError,
      });
      return;
    }

    if (!sessionId) {
      log.warn("Cannot retry analysis - no valid session", { sessionId });
      return;
    }

    // Message must be saved to DB before retry (wait for streaming to finish)
    if (state.isStreaming) {
      log.debug("Cannot retry analysis while streaming - message not saved yet", { messageId });
      return;
    }

    // Show pending badge (not a spinner, doesn't block send button)
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === messageId
          ? { ...m, analysisPending: true, analysisError: undefined }
          : m
      ),
      isAnalyzing: true,
    }));

    // Server runs a ~60s pronunciation model here — opt this one call into the
    // long-op budget instead of the fail-fast default. See network-resilience spec.
    const result = await api.analyzeMessage(sessionId, messageId, { timeout: HTTP_AI_TIMEOUT_MS });

    if (result.ok) {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId
            ? {
                ...m,
                // Generated schema only declares a subset of analysis fields;
                // runtime response includes the full ConversationAnalysis shape
                analysis: (result.data.analysis ?? undefined) as unknown as ConversationAnalysis | undefined,
                isAnalysisExpanded: false,
                analysisError: undefined,
                analysisPending: false,
              }
            : m
        ),
        isAnalyzing: false,
      }));
      AccessibilityInfo.announceForAccessibility?.("Analysis complete");
    } else {
      const errorInfo = resultToErrorInfo(result);
      log.warn("Analysis retry failed", { messageId, kind: result.kind, error: result.error });

      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId
            ? {
                ...m,
                analysisError: errorInfo,
                analysisPending: false,
              }
            : m
        ),
        isAnalyzing: false,
      }));
      AccessibilityInfo.announceForAccessibility?.("Analysis retry failed");
    }
  },

  /**
   * Toggle analysis card expanded state
   */
  toggleAnalysisExpanded: (messageId: string) => {
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId
          ? { ...m, isAnalysisExpanded: !m.isAnalysisExpanded }
          : m
      ),
    }));
  },

  /**
   * Clear error message
   */
  clearError: () => {
    set({ error: null, errorInfo: null });
  },
});
